import { FC, useEffect, useRef, useState } from "react";

import { Box, Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { useSanitizeRichTextHtml } from "@/common/helpers/sanitizeRichTextHtml";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ChevronDown, ChevronUp } from "@/icons/outlined";

const COLLAPSED_LINE_COUNT = 2;

interface CustomBlockProps {
  title: string;
  content: string;
  labelSx: object;
}

export const CustomBlock: FC<CustomBlockProps> = ({ title, content, labelSx }) => {
  const { t } = useTranslate();
  const sanitizedContent = useSanitizeRichTextHtml(content);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  // Whether the content exceeds two lines depends on fonts and width, which only the rendered DOM knows.
  useEffect(() => {
    const contentNode = contentRef.current;
    if (!contentNode || isExpanded) {
      return;
    }
    const observer = new ResizeObserver(() => setIsOverflowing(contentNode.scrollHeight > contentNode.clientHeight));
    observer.observe(contentNode);
    return () => observer.disconnect();
  }, [isExpanded, sanitizedContent]);

  const isCollapsed = isOverflowing && !isExpanded;

  return (
    <Stack sx={{ minWidth: 0 }} data-test="org-page-footer-custom-block">
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        {...(isOverflowing && {
          component: "button" as const,
          type: "button" as const,
          "aria-expanded": isExpanded,
          "aria-label": `${title}, ${t("organization", isExpanded ? "showLess" : "showMore")}`,
          onClick: () => setIsExpanded((expanded) => !expanded),
        })}
        sx={{
          minWidth: 0,
          padding: 0,
          border: "none",
          background: "none",
          color: "inherit",
          textAlign: "left",
          ...(isOverflowing && { cursor: "pointer" }),
        }}
      >
        <FormTypography
          sx={{
            ...labelSx,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </FormTypography>
        {isOverflowing && (
          <FormIcon size="medium" sx={(theme: Theme) => ({ color: theme.palette.forms.text.moderate, opacity: 0.6 })}>
            {isExpanded ? <ChevronUp /> : <ChevronDown />}
          </FormIcon>
        )}
      </Stack>
      <Box
        ref={contentRef}
        sx={(theme: Theme) => ({
          typography: { xs: "body2", sm: "body1" },
          position: "relative",
          color: theme.palette.forms.text.moderate,
          overflowWrap: "anywhere",
          "& p": { margin: 0 },
          "& a": { color: "inherit" },
          ...(!isExpanded && {
            maxHeight: `${COLLAPSED_LINE_COUNT}lh`,
            overflow: "hidden",
          }),
          ...(isCollapsed && {
            maskImage: "linear-gradient(to bottom, black 50%, transparent)",
          }),
        })}
        dangerouslySetInnerHTML={{ __html: sanitizedContent ?? "" }}
      />
    </Stack>
  );
};
